import { Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * POM-6506 AC 4 — which facility each row of "Also apply" takes the lot from.
 *
 * The dialog names only the edited row's facility, so a row at another facility reads as if it
 * took that facility's stock. It takes the same lot number and BUD from its own facility's stock,
 * and a facility without it leaves its rows out; both are said here.
 */

const rowsText = (rowCount) => `${rowCount} row${rowCount === 1 ? '' : 's'}`;

export const describeOtherFacilityStock = ({ lotNumber, tickedRowsByOtherFacility }) => {
  if (tickedRowsByOtherFacility.length === 0) return null;

  const counts = tickedRowsByOtherFacility
    .map(({ facilityName, rowCount }) => `${rowCount} at ${facilityName}`)
    .join(', ');

  return `Rows at another facility take lot ${lotNumber} from that facility's own stock: ${counts}.`;
};

export const describeRowsLeftOut = ({
  lotNumber,
  isManualLot,
  editedFacilityName,
  rowsLeftOutByFacility,
}) => {
  if (rowsLeftOutByFacility.length === 0) return [];

  if (isManualLot) {
    const total = rowsLeftOutByFacility.reduce((sum, { rowCount }) => sum + rowCount, 0);
    const where =
      rowsLeftOutByFacility.length === 1
        ? `at ${rowsLeftOutByFacility[0].facilityName}`
        : 'at other facilities';

    return [
      `A lot recorded by hand applies only at ${editedFacilityName ?? 'this facility'}, so ${rowsText(
        total
      )} ${where} ${total === 1 ? 'is' : 'are'} left out.`,
    ];
  }

  return rowsLeftOutByFacility.map(
    ({ facilityName, rowCount }) =>
      `Lot ${lotNumber} is not stocked at ${facilityName}, so ${rowsText(rowCount)} there ${
        rowCount === 1 ? 'is' : 'are'
      } left out.`
  );
};

const FACILITY_COUNTS = PropTypes.arrayOf(
  PropTypes.shape({ facilityName: PropTypes.string, rowCount: PropTypes.number })
);

const ChangeNdcLotBulkApplyFacilityNote = ({
  lotNumber,
  isManualLot,
  editedFacilityName,
  tickedRowsByOtherFacility,
  rowsLeftOutByFacility,
}) => {
  const lines = [
    describeOtherFacilityStock({ lotNumber, tickedRowsByOtherFacility }),
    ...describeRowsLeftOut({ lotNumber, isManualLot, editedFacilityName, rowsLeftOutByFacility }),
  ].filter(Boolean);
  if (lines.length === 0) return null;

  return (
    <Stack spacing={0.5} data-testid='bulk-apply-facility-note'>
      {lines.map((line) => (
        <Typography key={line} variant='body2' color='text.secondary'>
          {line}
        </Typography>
      ))}
    </Stack>
  );
};

ChangeNdcLotBulkApplyFacilityNote.propTypes = {
  lotNumber: PropTypes.string,
  isManualLot: PropTypes.bool,
  editedFacilityName: PropTypes.string,
  tickedRowsByOtherFacility: FACILITY_COUNTS,
  rowsLeftOutByFacility: FACILITY_COUNTS,
};

ChangeNdcLotBulkApplyFacilityNote.defaultProps = {
  lotNumber: '',
  isManualLot: false,
  editedFacilityName: null,
  tickedRowsByOtherFacility: [],
  rowsLeftOutByFacility: [],
};

export default ChangeNdcLotBulkApplyFacilityNote;
